import { useCallback, useMemo } from "react";
import { useACLContext } from "../context";
import type { ResourceType } from "@shield-acl/core";

/**
 * Interface com helpers CRUD para permissões
 */
export interface PermissionHelpers<TContext = any> {
  canRead: (resource: ResourceType, context?: TContext) => boolean;
  canCreate: (resource: ResourceType, context?: TContext) => boolean;
  canUpdate: (resource: ResourceType, context?: TContext) => boolean;
  canDelete: (resource: ResourceType, context?: TContext) => boolean;
  canManage: (resource: ResourceType, context?: TContext) => boolean;
  canList: (resource: ResourceType, context?: TContext) => boolean;
  canView: (resource: ResourceType, context?: TContext) => boolean;
  canEdit: (resource: ResourceType, context?: TContext) => boolean;
  canModify: (resource: ResourceType, context?: TContext) => boolean;
  canAccess: (resource: ResourceType, context?: TContext) => boolean;
}

/**
 * Hook que fornece helpers CRUD para verificação de permissões
 * Segue convenções comuns de nomenclatura de ações
 *
 * @returns Objeto com métodos helper para verificações CRUD
 *
 * @example
 * ```tsx
 * const { canRead, canCreate, canUpdate, canDelete, canManage } = usePermissionHelpers()
 *
 * // Verificações CRUD básicas
 * if (canRead("posts")) {
 *   // Pode ler posts
 * }
 *
 * if (canCreate("posts")) {
 *   // Pode criar posts
 * }
 *
 * // Com contexto
 * if (canUpdate("posts", { postId: 123, authorId: 456 })) {
 *   // Pode atualizar este post específico
 * }
 *
 * // Verificar permissão total (manage geralmente inclui todas as outras)
 * if (canManage("users")) {
 *   // Tem controle total sobre usuários
 * }
 * ```
 */
export function usePermissionHelpers<
  TContext = any,
>(): PermissionHelpers<TContext> {
  const { can, user } = useACLContext<TContext>();

  /**
   * Verifica permissão de leitura (read/view/list)
   */
  const canRead = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.read`, resource, context) ||
        can("read", resource, context) ||
        can(`${resource}.view`, resource, context) ||
        can("view", resource, context)
      );
    },
    [can],
  );

  /**
   * Verifica permissão de criação
   */
  const canCreate = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.create`, resource, context) ||
        can("create", resource, context) ||
        can(`${resource}.add`, resource, context) ||
        can("add", resource, context)
      );
    },
    [can],
  );

  /**
   * Verifica permissão de atualização
   */
  const canUpdate = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.update`, resource, context) ||
        can("update", resource, context) ||
        can(`${resource}.edit`, resource, context) ||
        can("edit", resource, context) ||
        can(`${resource}.modify`, resource, context) ||
        can("modify", resource, context)
      );
    },
    [can],
  );

  /**
   * Verifica permissão de exclusão
   */
  const canDelete = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.delete`, resource, context) ||
        can("delete", resource, context) ||
        can(`${resource}.remove`, resource, context) ||
        can("remove", resource, context)
      );
    },
    [can],
  );

  /**
   * Verifica permissão de gerenciamento total (todas as operações)
   */
  const canManage = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.manage`, resource, context) ||
        can("manage", resource, context) ||
        can(`${resource}.*`, resource, context) ||
        can("*", resource, context) ||
        // Se tem todas as permissões CRUD individuais, também pode gerenciar
        (canRead(resource, context) &&
          canCreate(resource, context) &&
          canUpdate(resource, context) &&
          canDelete(resource, context))
      );
    },
    [can, canRead, canCreate, canUpdate, canDelete],
  );

  /**
   * Verifica permissão de listagem (alias para read)
   */
  const canList = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.list`, resource, context) ||
        can("list", resource, context) ||
        canRead(resource, context)
      );
    },
    [can, canRead],
  );

  /**
   * Verifica permissão de visualização (alias para read)
   */
  const canView = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.view`, resource, context) ||
        can("view", resource, context) ||
        canRead(resource, context)
      );
    },
    [can, canRead],
  );

  /**
   * Verifica permissão de edição (alias para update)
   */
  const canEdit = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.edit`, resource, context) ||
        can("edit", resource, context) ||
        canUpdate(resource, context)
      );
    },
    [can, canUpdate],
  );

  /**
   * Verifica permissão de modificação (alias para update)
   */
  const canModify = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.modify`, resource, context) ||
        can("modify", resource, context) ||
        canUpdate(resource, context)
      );
    },
    [can, canUpdate],
  );

  /**
   * Verifica permissão de acesso geral (qualquer permissão no recurso)
   */
  const canAccess = useCallback(
    (resource: ResourceType, context?: TContext): boolean => {
      return (
        can(`${resource}.access`, resource, context) ||
        can("access", resource, context) ||
        canRead(resource, context) ||
        canCreate(resource, context) ||
        canUpdate(resource, context) ||
        canDelete(resource, context)
      );
    },
    [can, canRead, canCreate, canUpdate, canDelete],
  );

  // Retorna objeto memoizado com todos os helpers
  return useMemo(
    () => ({
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      canManage,
      canList,
      canView,
      canEdit,
      canModify,
      canAccess,
    }),
    [
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      canManage,
      canList,
      canView,
      canEdit,
      canModify,
      canAccess,
      user,
    ],
  );
}

/**
 * Hook para verificar permissões CRUD de um recurso específico
 * Útil quando você está trabalhando com um único recurso
 *
 * @param resource - O recurso para verificar permissões
 * @returns Objeto com métodos CRUD pré-configurados para o recurso
 *
 * @example
 * ```tsx
 * const postPermissions = useResourcePermissions("posts")
 *
 * if (postPermissions.canCreate()) {
 *   // Pode criar posts
 * }
 *
 * if (postPermissions.canUpdate({ postId: 123 })) {
 *   // Pode atualizar este post específico
 * }
 * ```
 */
export function useResourcePermissions<TContext = any>(resource: ResourceType) {
  const helpers = usePermissionHelpers<TContext>();

  return useMemo(
    () => ({
      canRead: (context?: TContext) => helpers.canRead(resource, context),
      canCreate: (context?: TContext) => helpers.canCreate(resource, context),
      canUpdate: (context?: TContext) => helpers.canUpdate(resource, context),
      canDelete: (context?: TContext) => helpers.canDelete(resource, context),
      canManage: (context?: TContext) => helpers.canManage(resource, context),
      canList: (context?: TContext) => helpers.canList(resource, context),
      canView: (context?: TContext) => helpers.canView(resource, context),
      canEdit: (context?: TContext) => helpers.canEdit(resource, context),
      canModify: (context?: TContext) => helpers.canModify(resource, context),
      canAccess: (context?: TContext) => helpers.canAccess(resource, context),
    }),
    [helpers, resource],
  );
}
